import type { ReactiveString, RequestOptions } from "../types/request";
import type { ServiceName } from "../types/service"

/**
 * @typedef {Object} listAddressbooksFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listAddressbooksFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addAddressbookFormData Addressbook's data
 * @prop {string|null=} organization_id - Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} contact_company_id - Contact Company Id
 */
export type addAddressbookFormData = {
	organization_id?: string|null;
	user_id?: string|null;
	contact_company_id?: string|null;
};

/**
 * @typedef {Object} editAddressbookFormData Addressbook's data
 * @prop {string|null=} organization_id - Organization Id
 * @prop {string|null=} user_id - User Id
 * @prop {string|null=} contact_company_id - Contact Company Id
 */
export type editAddressbookFormData = {
	organization_id?: string|null;
	user_id?: string|null;
	contact_company_id?: string|null;
};

/**
 * @typedef {Object} getMainShippingAddressAddressbookFormData Standart request
 * @prop {string} userType - User type: organization_member | individual_member
 * @prop {string|null=} organization_id - Organization ID
 * @prop {string|null=} user_id - User ID
 */
export type getMainShippingAddressAddressbookFormData = {
	userType: string;
	organization_id?: string|null;
	user_id?: string|null;
};

/**
 * @typedef {Object} listAddressFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listAddressFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addAddressFormData Address's data
 * @prop {string|null=} organization_id - Organization Id
 * @prop {string|null=} addressbook_id - Addressbook Id
 * @prop {string|null=} name - Name
 * @prop {string} address - Address, street number and name
 * @prop {string|null=} address_additional - Address Additional
 * @prop {string} zip - Zip
 * @prop {string|null=} dane_code - DANE code (Colombia only)
 * @prop {string} city - City
 * @prop {string} state - State
 * @prop {string} country - Country, 2 letter code
 * @prop {string|null=} coordinate - Coordinate
 * @prop {boolean|null=} validated - Is it a validated address or not
 * @prop {boolean|null=} billing_address - Is it a billing address or not
 * @prop {string} type - Address type : Commercial | Residential
 * @prop {boolean|null=} main_shipping_address - Main Shipping Address
 * @prop {boolean|null=} shipping_address - Is it a shipping address or not
 * @prop {boolean|null=} business_address - Is it a business address or not
 * @prop {boolean|null=} public_address - Is it a the public address of the organization or not
 * @prop {string|null=} phone - Phone number
 */
export type addAddressFormData = {
	organization_id?: string|null;
	addressbook_id?: string|null;
	name?: string|null;
	address: string;
	address_additional?: string|null;
	zip: string;
	dane_code?: string|null;
	city: string;
	state: string;
	country: string;
	coordinate?: string|null;
	validated?: boolean|null;
	billing_address?: boolean|null;
	type: string;
	main_shipping_address?: boolean|null;
	shipping_address?: boolean|null;
	business_address?: boolean|null;
	public_address?: boolean|null;
	phone?: string|null;
};

/**
 * @typedef {Object} editAddressFormData Address's data
 * @prop {string|null=} organization_id - Organization Id
 * @prop {string|null=} addressbook_id - Addressbook Id
 * @prop {string|null=} name - Name
 * @prop {string=} address - Address, street number and name
 * @prop {string|null=} address_additional - Address Additional
 * @prop {string=} zip - Zip
 * @prop {string|null=} dane_code - DANE code (Colombia only)
 * @prop {string=} city - City
 * @prop {string=} state - State
 * @prop {string=} country - Country, 2 letter code
 * @prop {string|null=} coordinate - Coordinate
 * @prop {boolean|null=} validated - Is it a validated address or not
 * @prop {boolean|null=} billing_address - Is it a billing address or not
 * @prop {string=} type - Address type : Commercial | Residential
 * @prop {boolean|null=} main_shipping_address - Main Shipping Address
 * @prop {boolean|null=} shipping_address - Is it a shipping address or not
 * @prop {boolean|null=} business_address - Is it a business address or not
 * @prop {boolean|null=} public_address - Is it a the public address of the organization or not
 * @prop {string|null=} phone - Phone number
 */
export type editAddressFormData = {
	organization_id?: string|null;
	addressbook_id?: string|null;
	name?: string|null;
	address?: string;
	address_additional?: string|null;
	zip?: string;
	dane_code?: string|null;
	city?: string;
	state?: string;
	country?: string;
	coordinate?: string|null;
	validated?: boolean|null;
	billing_address?: boolean|null;
	type?: string;
	main_shipping_address?: boolean|null;
	shipping_address?: boolean|null;
	business_address?: boolean|null;
	public_address?: boolean|null;
	phone?: string|null;
};

/**
 * @typedef {Object} getPricingContinentAddressFormData Standart request
 * @prop {string|null=} organization_id - Organization ID
 * @prop {string|null=} user_id - User ID
 */
export type getPricingContinentAddressFormData = {
	organization_id?: string|null;
	user_id?: string|null;
};

/**
 * @typedef {Object} listCountriesFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listCountriesFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} viewManyCountryFormData
 * @prop {{}} countries - List of country_id or country_alpha2
 */
export type viewManyCountryFormData = {
	countries: {};
};

/**
 * @typedef {Object} addCountryFormData Country's data
 * @prop {string} continent_alpha2 - Continent Alpha2
 * @prop {string} continent_name - Continent Name
 * @prop {string} continent_sub_region - Continent Sub Region
 * @prop {string} country_name - Country Name
 * @prop {string} country_name_official - Country Name Official
 * @prop {string} country_sovereignty - Country Sovereignty
 * @prop {string} country_alpha2 - Country Alpha2
 * @prop {string} country_alpha3 - Country Alpha3
 * @prop {string} country_numeric_code - Country Numeric Code
 * @prop {boolean} address_validation_required - Address Validation Required
 * @prop {boolean=} personal_identification_number_required - Personal Identification Number Required
 * @prop {boolean=} business_registration_number_required - Business Registration Number Required
 */
export type addCountryFormData = {
	continent_alpha2: string;
	continent_name: string;
	continent_sub_region: string;
	country_name: string;
	country_name_official: string;
	country_sovereignty: string;
	country_alpha2: string;
	country_alpha3: string;
	country_numeric_code: string;
	address_validation_required: boolean;
	personal_identification_number_required?: boolean;
	business_registration_number_required?: boolean;
};

/**
 * @typedef {Object} editCountryFormData Country's data
 * @prop {string=} continent_alpha2 - Continent Alpha2
 * @prop {string=} continent_name - Continent Name
 * @prop {string=} continent_sub_region - Continent Sub Region
 * @prop {string=} country_name - Country Name
 * @prop {string=} country_name_official - Country Name Official
 * @prop {string=} country_sovereignty - Country Sovereignty
 * @prop {string=} country_alpha2 - Country Alpha2
 * @prop {string=} country_alpha3 - Country Alpha3
 * @prop {string=} country_numeric_code - Country Numeric Code
 * @prop {boolean=} address_validation_required - Address Validation Required
 * @prop {boolean=} personal_identification_number_required - Personal Identification Number Required
 * @prop {boolean=} business_registration_number_required - Business Registration Number Required
 */
export type editCountryFormData = {
	continent_alpha2?: string;
	continent_name?: string;
	continent_sub_region?: string;
	country_name?: string;
	country_name_official?: string;
	country_sovereignty?: string;
	country_alpha2?: string;
	country_alpha3?: string;
	country_numeric_code?: string;
	address_validation_required?: boolean;
	personal_identification_number_required?: boolean;
	business_registration_number_required?: boolean;
};

/**
 * @typedef {Object} listCountrySubdivisionsFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listCountrySubdivisionsFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addCountrySubdivisionFormData Country Subdivision's data
 * @prop {string} country_id - Country Id
 * @prop {string} name - Name
 * @prop {string} code - Code
 * @prop {Number} status - Status (0, 1, 99)
 */
export type addCountrySubdivisionFormData = {
	country_id: string;
	name: string;
	code: string;
	status: Number;
};

/**
 * @typedef {Object} editCountrySubdivisionFormData Country Subdivision's data
 * @prop {string=} country_id - Country Id
 * @prop {string=} name - Name
 * @prop {string=} code - Code
 * @prop {Number=} status - Status (0, 1, 99)
 */
export type editCountrySubdivisionFormData = {
	country_id?: string;
	name?: string;
	code?: string;
	status?: Number;
};

/**
 * @typedef {Object} listOfficeMemberFormData Pagination parameters (can be provided via query parameters for GET requests).
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listOfficeMemberFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addOfficeMemberFormData Office Member's data
 * @prop {string} office_id - Office ID
 * @prop {string} user_id - User ID
 */
export type addOfficeMemberFormData = {
	office_id: string;
	user_id: string;
};

/**
 * @typedef {Object} editOfficeMemberFormData Office Member's data
 * @prop {string} office_id - Office ID
 * @prop {string} user_id - User ID
 */
export type editOfficeMemberFormData = {
	office_id: string;
	user_id: string;
};

/**
 * @typedef {Object} listOfficeFormData Pagination parameters (can be provided via query parameters for GET requests).
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listOfficeFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addOfficeFormData Office's data
 * @prop {string} organization_id - Organization ID
 * @prop {string} address_id - Address ID
 * @prop {string} office_name - Office Name
 * @prop {boolean} main_office - Is it a main office
 * @prop {string} contact_id - Contact's ID
 */
export type addOfficeFormData = {
	organization_id: string;
	address_id: string;
	office_name: string;
	main_office: boolean;
	contact_id: string;
};

/**
 * @typedef {Object} editOfficeFormData Office's data
 * @prop {string} organization_id - Organization ID
 * @prop {string} address_id - Address ID
 * @prop {string=} office_name - Office Name
 * @prop {boolean=} main_office - Is it a main office
 * @prop {string=} contact_id - Contact's ID
 */
export type editOfficeFormData = {
	organization_id: string;
	address_id: string;
	office_name?: string;
	main_office?: boolean;
	contact_id?: string;
};

/**
 * @typedef {Object} businessAddressOrganizationAddressFormData Address's data
 * @prop {{}} address - Address data
 * @prop {string} organization_id - Organization Id
 * @prop {boolean|null=} replace_existing_billing_address - Only required when setting the address as the billing address
 * @prop {boolean|null=} replace_existing_shipping_address - Only required when setting the address as the shipping address
 */
export type businessAddressOrganizationAddressFormData = {
	address: {};
	organization_id: string;
	replace_existing_billing_address?: boolean|null;
	replace_existing_shipping_address?: boolean|null;
};

/**
 * @typedef {Object} billingAddressOrganizationAddressFormData Address's data
 * @prop {{}} address - Address data
 * @prop {string} organization_id - Organization Id
 */
export type billingAddressOrganizationAddressFormData = {
	address: {};
	organization_id: string;
};

/**
 * @typedef {Object} warehouseAddressOrganizationAddressFormData Address's data
 * @prop {{}} address - Address data
 * @prop {string} organization_id - Organization Id
 * @prop {boolean|null=} replace_existing_billing_address - Only required when setting the address as the billing address
 * @prop {boolean|null=} replace_existing_shipping_address - Only required when setting the address as the shipping address
 * @prop {{}} warehouse - Warehouse's data
 * @prop {{}=} storage_method - storage_method
 * @prop {{}=} contact - Contact for the warehouse
 */
export type warehouseAddressOrganizationAddressFormData = {
	address: {};
	organization_id: string;
	replace_existing_billing_address?: boolean|null;
	replace_existing_shipping_address?: boolean|null;
	warehouse: {};
	storage_method?: {};
	contact?: {};
};

/**
 * @typedef {Object} listWarehouseMemberFormData Pagination parameters (can be provided via query parameters for GET requests).
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listWarehouseMemberFormData = {
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addWarehouseMemberFormData WarehouseMember's data
 * @prop {string} warehouse_id - Warehouse ID
 * @prop {string} user_id - User ID
 */
export type addWarehouseMemberFormData = {
	warehouse_id: string;
	user_id: string;
};

/**
 * @typedef {Object} editWarehouseMemberFormData WarehouseMember's data
 * @prop {string} warehouse_id - Warehouse ID
 * @prop {string} user_id - User ID
 */
export type editWarehouseMemberFormData = {
	warehouse_id: string;
	user_id: string;
};

/**
 * @typedef {Object} listWarehouseFormData
 * @prop {{}=} filters
 * @prop {Number=} limit - Pagination Limit per page
 * @prop {Number=} page - Page Number to pull
 * @prop {{}=} order - Ordering rules, as "field_name" => "DIR" (i.e. {created: "DESC"})
 * @prop {{}=} field_list - List of fields to return
 */
export type listWarehouseFormData = {
	filters?: {};
	limit?: Number;
	page?: Number;
	order?: {};
	field_list?: {};
};

/**
 * @typedef {Object} addWarehouseFormData Warehouse's data
 * @prop {string} organization_id - Organization Id
 * @prop {string|null=} address_id - Address Id
 * @prop {string|null=} packing_strategy_id - Packing Strategy Id
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string} warehouse_name - Warehouse Name
 * @prop {boolean} main_warehouse - Main Warehouse
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {string} phone - Phone
 * @prop {{}=} address - address
 * @prop {{}=} inventories - storage_method
 * @prop {{}} contact - Contact for the warehouse
 */
export type addWarehouseFormData = {
	organization_id: string;
	address_id?: string|null;
	packing_strategy_id?: string|null;
	contact_id?: string|null;
	warehouse_name: string;
	main_warehouse: boolean;
	status?: Number;
	phone: string;
	address?: {};
	inventories?: {};
	contact: {};
};

/**
 * @typedef {Object} editWarehouseFormData Warehouse's data
 * @prop {string} id - id of the warehouse that will be modified.
 * @prop {string} organization_id - Organization Id
 * @prop {string|null=} address_id - Address Id
 * @prop {string|null=} packing_strategy_id - Packing Strategy Id
 * @prop {string|null=} contact_id - Contact Id
 * @prop {string} warehouse_name - Warehouse Name
 * @prop {boolean} main_warehouse - Main Warehouse
 * @prop {Number=} status - Status (0, 1, 99)
 * @prop {string} phone - Phone
 * @prop {{}=} inventories - storage_method
 * @prop {{}=} address - address
 * @prop {{}=} contact - contact
 */
export type editWarehouseFormData = {
	id: string;
	organization_id: string;
	address_id?: string|null;
	packing_strategy_id?: string|null;
	contact_id?: string|null;
	warehouse_name: string;
	main_warehouse: boolean;
	status?: Number;
	phone: string;
	inventories?: {};
	address?: {};
	contact?: {};
};

const service = {
	name: "ABM" as ServiceName,
	version: 1
};

export const useABMApi = () => {
	/**
	 * Call an endpoint on the ABM
	 *
	 * Sets the service to the Request and forwards it to the TengivaApi.call() function
	 * @param {ReactiveString} endpoint - Endpoint to request
	 * @param {RequestOptions} options - Additonal options for the Request
	 * @returns {Promise<any>}
	 */
	const call = async <T = any>(endpoint: ReactiveString, options: RequestOptions) => {
		options = {
			...options,
			service
		};
		return await useTengivaApi<T>(endpoint, options);
	};

	/**
	 * Index method
	 * 
	 * @param {listAddressbooksFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listAddressbooks = async <T = any>(formData: listAddressbooksFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/addressbooks/`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} addressbook - Addressbook id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAddressbook = async <T = any>(addressbook: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addressbooks/view`,
			[
				addressbook
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * GetOrCreateAddressbook method
	 * 
	 * @param {ReactiveString} organization_id
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getOrCreateAddressbookAddressbook = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addressbooks/getOrCreateAddressbook`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Addressbook matching an Organization ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} organization_id - Organization
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByOrganizationIdAddressbook = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addressbooks/viewAllByOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get all Addressbook matching a user ID
	 * 
	 * Supports all the paginate query params:
	 * "?field_list=id,email,first_name&order[field_name]=DIRECTION&limit=10&page=3"
	 * 
	 * @param {ReactiveString} user_id - User
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAllByUserIdAddressbook = async <T = any>(user_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addressbooks/viewAllByUserId`,
			[
				user_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addAddressbookFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addAddressbook = async <T = any>(formData: addAddressbookFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/addressbooks/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} addressbook - Addressbook id.
	 * @param {editAddressbookFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editAddressbook = async <T = any>(addressbook: ReactiveString, formData?: editAddressbookFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addressbooks/edit`,
			[
				addressbook
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} addressbook - Addressbook id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteAddressbook = async <T = any>(addressbook: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addressbooks/delete`,
			[
				addressbook
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get main shipping address for an organization or individual based on userType request parameter
	 * 
	 * @param {getMainShippingAddressAddressbookFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getMainShippingAddressAddressbook = async <T = any>(formData: getMainShippingAddressAddressbookFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/addressbooks/getMainShippingAddress`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List addresses
	 * 
	 * @param {listAddressFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listAddress = async <T = any>(formData?: listAddressFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(`/addresses/list`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Create an address
	 * 
	 * Address will be verified if it is correct to PSM EasyPost service
	 * 
	 * @param {addAddressFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addAddress = async <T = any>(formData: addAddressFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/addresses/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit an address
	 * 
	 * @param {ReactiveString} address - Address id.
	 * @param {editAddressFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editAddress = async <T = any>(address: ReactiveString, formData?: editAddressFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addresses/edit`,
			[
				address
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Validate an address
	 * 
	 * @param {ReactiveString} address
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setValidatedAddress = async <T = any>(address: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addresses/setValidated`,
			[
				address
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific address by address id
	 * 
	 * @param {ReactiveString} address - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewAddress = async <T = any>(address: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/addresses/view`,
			[
				address
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View a list of address by address id
	 * 
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getAddressesAddress = async <T = any>(formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/addresses/getAddresses`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete specific address by address id
	 * 
	 * @param {ReactiveString} address - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteAddress = async <T = any>(address: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addresses/delete`,
			[
				address
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get Shipping (and optional Billing Address) by User or Organization ID
	 * 
	 * Found addresses will include the Office's and the Warehouse's data
	 * 
	 * @param {ReactiveString} id - 
	 * @param {ReactiveString} include_billing - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getShippingAddressAddress = async <T = any>(id: ReactiveString, include_billing: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addresses/getShippingAddress`,
			[
				id, 
				include_billing
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get getBusinessAddress for an organization_id
	 * 
	 * @param {ReactiveString} organization_id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getBusinessAddressAddress = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addresses/getBusinessAddress`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get getBusinessAddress for an organization_id
	 * 
	 * @param {ReactiveString} organization_id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getSubscriptionAddressesForOrganizationIdAddress = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addresses/getSubscriptionAddressesForOrganizationId`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get getPlatformPublicAddress for an organization_id
	 * 
	 * @param {ReactiveString} organization_id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getPlatformPublicAddressAddress = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/addresses/getPlatformPublicAddress`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get default billing and shipping addresses for an organization_id
	 * 
	 * @param {ReactiveString} organization_id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOrgAddressesAddress = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addresses/viewOrgAddresses`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get default business, billing and shipping addresses for an organization_id
	 * 
	 * @param {ReactiveString} organization_id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewDefaultAddressesAddress = async <T = any>(organization_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addresses/viewDefaultAddresses`,
			[
				organization_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get default billing and shipping addresses for a user
	 * 
	 * @param {ReactiveString} user_id - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewUserDefaultAddressesAddress = async <T = any>(user_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/addresses/viewUserDefaultAddresses`,
			[
				user_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View addresses public info for a list of organization ids
	 * 
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewPublicAddressForOrganizationsIdsAddress = async <T = any>(formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/addresses/viewPublicAddressForOrganizationsIds`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get continent name for an organization or individual based on identity user type
	 * Returns "North America" as default value or if no country is found.
	 * 
	 * @param {getPricingContinentAddressFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getPricingContinentAddress = async <T = any>(formData?: getPricingContinentAddressFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/addresses/getPricingContinent`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listCountriesFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCountries = async <T = any>(formData?: listCountriesFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/countries/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} country_id - Country id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCountry = async <T = any>(country_id: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/countries/view`,
			[
				country_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * ViewMany method
	 * 
	 * @param {viewManyCountryFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewManyCountry = async <T = any>(formData: viewManyCountryFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/countries/viewMany`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addCountryFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCountry = async <T = any>(formData: addCountryFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/countries/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} country - Country id.
	 * @param {editCountryFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCountry = async <T = any>(country: ReactiveString, formData?: editCountryFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/countries/edit`,
			[
				country
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} country - Country id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCountry = async <T = any>(country: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/countries/delete`,
			[
				country
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * getCountryByAlphaTwo method
	 * 
	 * @param {ReactiveString} alpha2
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const getCountryByAlphaTwoCountry = async <T = any>(alpha2: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/countries/getCountryByAlphaTwo`,
			[
				alpha2
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Index method
	 * 
	 * @param {listCountrySubdivisionsFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listCountrySubdivisions = async <T = any>(formData?: listCountrySubdivisionsFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/country_subdivisions/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View method
	 * 
	 * @param {ReactiveString} countrySubdivision - Country Subdivision id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewCountrySubdivision = async <T = any>(countrySubdivision: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/country_subdivisions/view`,
			[
				countrySubdivision
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add method
	 * 
	 * @param {addCountrySubdivisionFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addCountrySubdivision = async <T = any>(formData: addCountrySubdivisionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/country_subdivisions/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit method
	 * 
	 * @param {ReactiveString} countrySubdivision - Country Subdivision id.
	 * @param {editCountrySubdivisionFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editCountrySubdivision = async <T = any>(countrySubdivision: ReactiveString, formData?: editCountrySubdivisionFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/country_subdivisions/edit`,
			[
				countrySubdivision
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete method
	 * 
	 * @param {ReactiveString} countrySubdivision - Country Subdivision id.
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteCountrySubdivision = async <T = any>(countrySubdivision: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/country_subdivisions/delete`,
			[
				countrySubdivision
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View all warehouses of an organization id in a Marketplace public context
	 * 
	 * @param {ReactiveString} organizationId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewWarehousesForOrganizationIdMarketplace = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/marketplaces/viewWarehousesForOrganizationId`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of office members
	 * 
	 * @param {listOfficeMemberFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listOfficeMember = async <T = any>(formData?: listOfficeMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/officeMembers/list`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add an office member
	 * 
	 * @param {addOfficeMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOfficeMember = async <T = any>(formData: addOfficeMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/officeMembers/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit an office member
	 * 
	 * @param {ReactiveString} member
	 * @param {editOfficeMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editOfficeMember = async <T = any>(member: ReactiveString, formData: editOfficeMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/officeMembers/edit`,
			[
				member
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific member by id
	 * 
	 * @param {ReactiveString} member - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOfficeMember = async <T = any>(member: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/officeMembers/view`,
			[
				member
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * delete specific office member by id
	 * 
	 * @param {ReactiveString} member - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteOfficeMember = async <T = any>(member: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/officeMembers/delete`,
			[
				member
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of offices
	 * 
	 * @param {listOfficeFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listOffice = async <T = any>(formData?: listOfficeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/offices/list`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add an office
	 * 
	 * @param {addOfficeFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addOffice = async <T = any>(formData: addOfficeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/offices/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit an offices
	 * 
	 * @param {ReactiveString} office
	 * @param {editOfficeFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editOffice = async <T = any>(office: ReactiveString, formData: editOfficeFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/offices/edit`,
			[
				office
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific office by office id
	 * 
	 * @param {ReactiveString} office - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewOffice = async <T = any>(office: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/offices/view`,
			[
				office
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * delete specific office by office id
	 * 
	 * @param {ReactiveString} office - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteOffice = async <T = any>(office: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/offices/delete`,
			[
				office
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Set office as Billing address
	 * 
	 * @param {ReactiveString} office
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setBillingAddressOffice = async <T = any>(office: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/offices/markBilling`,
			[
				office
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Set office as Shipping address
	 * 
	 * @param {ReactiveString} office
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setShippingAddressOffice = async <T = any>(office: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/offices/markShipping`,
			[
				office
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * businessAddress method - Create address if no address_id is provided, otherwise update existing address
	 * If address is set to be created, some address parameters are required (address, zip, city, state, country, type) and address_additional needs to be present.
	 * It will not create a new address for an address with the same data.
	 * Added organization_id as required parameter in order to create or get addressbook id without needing to make an additional call on the frontend
	 * If 'replace_existing_billing_address' is set to true, any other address of the organization will be set as a non billing address
	 * If 'replace_existing_shipping_address' is set to true, any other address of the organization will be set as a non shipping address
	 * To access the endpoint user should belong to address organization or should belong to Tengiva organization
	 * 
	 * @param {ReactiveString} address_id - Address id.
	 * @param {businessAddressOrganizationAddressFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const businessAddressOrganizationAddress = async <T = any>(address_id: ReactiveString, formData: businessAddressOrganizationAddressFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organization_addresses/businessAddress`,
			[
				address_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * billingAddress method - Create billing address if no address_id is provided, otherwise update existing one.
	 * If any other billing address exists they will be set as non billing addresses (Only one address will be set as a billing one)
	 * If address is set to be created and any billing address exists it will overwrite the last of them.
	 * If address is set to be created and no billing addresses exists but there is an address with the same data it will overwrite and set it as a billing address.
	 * If address is set to be created and no billing addresses exists, some address parameters are required (address, zip, city, state, country, type) and address_additional needs to be present.
	 * Added organization_id as required parameter in order to create or get addressbook id without needing to make an additional call on the frontend.
	 * To access the endpoint user should belong to address organization or should belong to Tengiva organization.
	 * 
	 * @param {ReactiveString} address_id - Address id.
	 * @param {billingAddressOrganizationAddressFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const billingAddressOrganizationAddress = async <T = any>(address_id: ReactiveString, formData: billingAddressOrganizationAddressFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organization_addresses/billingAddress`,
			[
				address_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * warehouseAddress method - Create a warehouse if no warehouse_id is provided, otherwise update existing warehouse
	 * Creates a new address if no address id is provided and the same address doesn't exits
	 * If address is set to be created (address.id is not present), some address parameters are required (address, zip, city, state, country, type) and address_additional needs to be present.
	 * Added organization_id as required parameter in order to create or get addressbook id without needing to make an additional call on the frontend
	 * If 'replace_existing_billing_address' is set to true, any other address of the organization will be set as a non billing address
	 * If 'replace_existing_shipping_address' is set to true, any other address of the organization will be set as a non shipping address
	 * 
	 * Creates a new shipping address at the PSM using address, warehouse name, company name and phone number data
	 * Creates a new contact and contact methods for the organization using "contact" data
	 * Creates a new Inventory and Storage Methods using "storage_method" data
	 * To access the endpoint user should belong to address organization or should belong to Tengiva organization
	 * 
	 * @param {ReactiveString} warehouse_id - Warehouse id.
	 * @param {warehouseAddressOrganizationAddressFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const warehouseAddressOrganizationAddress = async <T = any>(warehouse_id: ReactiveString, formData: warehouseAddressOrganizationAddressFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organization_addresses/warehouseAddress`,
			[
				warehouse_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * setPublicAddress method - Set address_id as public address
	 * Set all other existing public addresses for the organization as non public
	 * 
	 * @param {ReactiveString} address_id - Address id.
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setPublicAddressOrganizationAddress = async <T = any>(address_id: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/organization_addresses/setPublicAddress`,
			[
				address_id
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of warehouse members
	 * 
	 * @param {listWarehouseMemberFormData|{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listWarehouseMember = async <T = any>(formData?: listWarehouseMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(`/warehouseMembers/`);

		// Change request type when the request has a body
		if (Object.keys(formData).length > 0) {
			requestOptions.body = formData;
			requestOptions.method = "POST";
		}

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add an warehouse member
	 * 
	 * @param {addWarehouseMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addWarehouseMember = async <T = any>(formData: addWarehouseMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/warehouseMembers/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit an warehouse member
	 * 
	 * @param {ReactiveString} member
	 * @param {editWarehouseMemberFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editWarehouseMember = async <T = any>(member: ReactiveString, formData: editWarehouseMemberFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/warehouseMembers/edit`,
			[
				member
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific member by id
	 * 
	 * @param {ReactiveString} member - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewWarehouseMember = async <T = any>(member: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/warehouseMembers/view`,
			[
				member
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * delete specific warehouse member by id
	 * 
	 * @param {ReactiveString} member - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteWarehouseMember = async <T = any>(member: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/warehouseMembers/delete`,
			[
				member
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * convertIdsToNames function
	 * 
	 * @param {ReactiveString} test
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const convertIdsToNamesWarehouse = async <T = any>(test: ReactiveString, formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/warehouses/convertIdsToNames`,
			[
				test
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Get list of warehouse and their associated address
	 * 
	 * @param {listWarehouseFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listWarehouse = async <T = any>(formData: listWarehouseFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/warehouses/list`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Add an warehouse
	 * 
	 * @param {addWarehouseFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const addWarehouse = async <T = any>(formData: addWarehouseFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/warehouses/add`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Edit an warehouse
	 * 
	 * @param {ReactiveString} warehouse
	 * @param {editWarehouseFormData|{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const editWarehouse = async <T = any>(warehouse: ReactiveString, formData: editWarehouseFormData|{}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/warehouses/edit`,
			[
				warehouse
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View specific warehouse by id
	 * 
	 * @param {ReactiveString} warehouse - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewWarehouse = async <T = any>(warehouse: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/warehouses/view`,
			[
				warehouse
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * List a short summary of a given set of warehouses
	 * 
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listSummaryWarehouse = async <T = any>(formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(`/warehouses/listSummary`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View all warehouses of an organization id
	 * 
	 * @param {ReactiveString} organizationId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationIdWarehouse = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/warehouses/viewForOrganizationId`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View warehouses basic info for a list of organization ids
	 * 
	 * @param {{}} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewForOrganizationsIdsWarehouse = async <T = any>(formData: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "POST",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(`/warehouses/viewForOrganizationsIds`);

		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * View all warehouses of an organization id
	 * 
	 * @param {ReactiveString} organizationId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const viewDetailsForOrganizationWarehouse = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/warehouses/viewDetailsForOrganization`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Detailed View all warehouses of an organization id
	 * 
	 * @param {ReactiveString} organizationId - 
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const listDetailsForOrganizationsWarehouse = async <T = any>(organizationId: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "GET",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/warehouses/listDetailsForOrganizations`,
			[
				organizationId
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Delete specific warehouse by id
	 * 
	 * @param {ReactiveString} warehouse - URL's UUID resolved from the automatically
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const deleteWarehouse = async <T = any>(warehouse: ReactiveString, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "DELETE",
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/warehouses/delete`,
			[
				warehouse
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Set warehouse as Main warehouse
	 * 
	 * @param {ReactiveString} warehouse
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setMainWarehouseWarehouse = async <T = any>(warehouse: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/warehouses/setMainWarehouse`,
			[
				warehouse
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Set warehouse as Billing address
	 * 
	 * @param {ReactiveString} warehouse
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setBillingAddressWarehouse = async <T = any>(warehouse: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: false,
		};

		const endpoint = formatEndpoint(
			`/warehouses/markBilling`,
			[
				warehouse
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};

	/**
	 * Set warehouse as Shipping address
	 * 
	 * @param {ReactiveString} warehouse
	 * @param {{}=} formData
	 * @param {RequestOptions=} options
	 * @returns {Promise<any>}
	 */
	const setShippingAddressWarehouse = async <T = any>(warehouse: ReactiveString, formData?: {}, options?: RequestOptions) => {
		let requestOptions: RequestOptions = {
			method: "PATCH",
			body: formData,
			auth: true,
		};

		const endpoint = formatEndpoint(
			`/warehouses/markShipping`,
			[
				warehouse
			]
		);
		if (options) {
			requestOptions = {
				...requestOptions,
				...options
			}
		}

		return await call<T>(endpoint, requestOptions);
	};


	return {
		call,
		listAddressbooks,
		viewAddressbook,
		getOrCreateAddressbookAddressbook,
		viewAllByOrganizationIdAddressbook,
		viewAllByUserIdAddressbook,
		addAddressbook,
		editAddressbook,
		deleteAddressbook,
		getMainShippingAddressAddressbook,
		listAddress,
		addAddress,
		editAddress,
		setValidatedAddress,
		viewAddress,
		getAddressesAddress,
		deleteAddress,
		getShippingAddressAddress,
		getBusinessAddressAddress,
		getSubscriptionAddressesForOrganizationIdAddress,
		getPlatformPublicAddressAddress,
		viewOrgAddressesAddress,
		viewDefaultAddressesAddress,
		viewUserDefaultAddressesAddress,
		viewPublicAddressForOrganizationsIdsAddress,
		getPricingContinentAddress,
		listCountries,
		viewCountry,
		viewManyCountry,
		addCountry,
		editCountry,
		deleteCountry,
		getCountryByAlphaTwoCountry,
		listCountrySubdivisions,
		viewCountrySubdivision,
		addCountrySubdivision,
		editCountrySubdivision,
		deleteCountrySubdivision,
		viewWarehousesForOrganizationIdMarketplace,
		listOfficeMember,
		addOfficeMember,
		editOfficeMember,
		viewOfficeMember,
		deleteOfficeMember,
		listOffice,
		addOffice,
		editOffice,
		viewOffice,
		deleteOffice,
		setBillingAddressOffice,
		setShippingAddressOffice,
		businessAddressOrganizationAddress,
		billingAddressOrganizationAddress,
		warehouseAddressOrganizationAddress,
		setPublicAddressOrganizationAddress,
		listWarehouseMember,
		addWarehouseMember,
		editWarehouseMember,
		viewWarehouseMember,
		deleteWarehouseMember,
		convertIdsToNamesWarehouse,
		listWarehouse,
		addWarehouse,
		editWarehouse,
		viewWarehouse,
		listSummaryWarehouse,
		viewForOrganizationIdWarehouse,
		viewForOrganizationsIdsWarehouse,
		viewDetailsForOrganizationWarehouse,
		listDetailsForOrganizationsWarehouse,
		deleteWarehouse,
		setMainWarehouseWarehouse,
		setBillingAddressWarehouse,
		setShippingAddressWarehouse,
	}
};